import { QueryClient, QueryCache, MutationCache } from '@tanstack/react-query'
import { logError } from '@/lib/error/error-logger'
import { toast } from 'sonner'
import { STALE_TIMES, GC_TIMES } from '@/lib/api'

/**
 * 4xx codes the UI already handles locally — a global toast would be redundant noise.
 *   401 → auth retry / signout flow in lib/api/client.ts
 *   409 → idempotency conflicts (action already applied)
 *   422 → form validation (errors rendered inline)
 *   429 → rate limit (UI surfaces cooldown)
 */
export const SILENT_TOAST_STATUSES: ReadonlySet<number> = new Set([401, 409, 422, 429])

/**
 * HabitPack Global Error Handler for API requests.
 */
export const handleGlobalError = (error: any, type: 'api-query' | 'api-mutation') => {
  const status = error?.status || error?.response?.status | 0

  // F-18 (HAB-54): only send server (5xx) and network/no-status (0) failures to the
  // console registry. Expected client codes (401/403/409/422/429 and other 4xx) are
  // handled locally — logging them spammed the console on every transient retry.
  if (status >= 500 || status === 0) {
    logError(error, { type, status })
  }

  // Visual feedback for specific failure codes
  if (status >= 500) {
    toast.error('Signal Lost: Internal system error', {
      description: 'The master registry is unreachable. Protocol re-init suggested.',
      icon: '📡',
      duration: 10000,
    })
  } else if (status === 403) {
    toast.error('Access Denied: Level unauthorized', {
      description: 'Credentials breach or insufficient hunter rank detected.',
      icon: '🔐',
      duration: 6000,
    })
  } else if (status !== 0 && !SILENT_TOAST_STATUSES.has(status)) {
    // 0 is often just a cancelled request or network loss handled by separate boundary
    toast.error(`Transmission Error: ${error.message || 'Unknown protocol failure'}`, {
      icon: '⚠️',
    })
  }
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error) => handleGlobalError(error, 'api-query'),
  }),
  mutationCache: new MutationCache({
    onError: (error) => handleGlobalError(error, 'api-mutation'),
  }),
  defaultOptions: {
    queries: {
      staleTime: STALE_TIMES.NORMAL,
      gcTime: GC_TIMES.LONG,
      retry: (failureCount, error: any) => {
        const errorStatus = error?.status || error?.response?.status
        if (errorStatus === 401 || errorStatus === 403 || errorStatus === 404) {
          return false
        }
        return failureCount < 3
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      refetchOnWindowFocus: false,
      refetchOnReconnect: 'always',
    },
    mutations: {
      retry: 1,
      retryDelay: 1000,
    },
  },
})
